// Util
import { useSpecificQuery, useSpecificMutation } from 'services/util/queryUtil';
import { getCookie } from 'utils/CookieUtil';

// export const StaleTime = 60 * 1000;

const GetSessionId = () => {
    const sessionIdParentKey = 'utag_main';
    const value = getCookie(sessionIdParentKey);
    if (!value) return value;

    const sessionIdKey = 'ses_id';
    const sessionId = value
        ?.toString()
        ?.split(`${sessionIdKey}:`)[1]
        ?.split(';')[0];
    return sessionId;
};

export const GetOMTHeaders = () => {
    return {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Garmin-Client-Name': 'ConnectWeb',
        'Garmin-Client-Version': window['URL_BUST_VALUE']
            .split('.')
            .slice(0, 3)
            .join('.'),
        'Garmin-Client-Platform': 'web',
        'Garmin-Client-Platform-Version': window.navigator.userAgent,
        'Garmin-Client-SessionId': GetSessionId()
    };
};

/**
 * @typedef {import('@tanstack/react-query').UseQueryOptions} UseQueryOptions
 * @typedef {import('@tanstack/react-query').QueryClient} QueryClient
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').UndefinedInitialDataOptions<TData, TError>} UndefinedInitialDataOptions
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').DefinedInitialDataOptions<TData, TError>} DefinedInitialDataOptions
 */

/**
 * @template TData
 * @template TVariables
 * @template [TError=Error]
 * @template [TContext=any]
 * @typedef {import('@tanstack/react-query').UseMutationOptions<TData, TError, TVariables, TContext>} UseMutationOptions
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').UseQueryResult<TData, TError>} UseQueryResult
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').DefinedUseQueryResult<TData, TError>} DefinedUseQueryResult
 */

/**
 * @template TData
 * @template TVariables
 * @template [TError=Error]
 * @template [TContext=any]
 * @typedef {import('@tanstack/react-query').UseMutationResult<TData, TError, TVariables, TContext>} UseMutationResult
 */

/**
 * Allows a specific return type to be mapped to useQuery with a special query key added for the Session ID.
 * This prevents any potential data persistence when the Session ID changes by forcing a refetch.
 *
 * @template TData
 * @param {UndefinedInitialDataOptions<TData> | DefinedInitialDataOptions<TData> | UseQueryOptions} options
 * @param {QueryClient | undefined} queryClient
 * @returns {UseQueryResult<TData> | DefinedUseQueryResult<TData>}
 */
export const OMTSessionUseSpecificQuery = (options, queryClient = undefined) => {
    options.queryKey = [...options.queryKey, GetSessionId()];
    return useSpecificQuery(options, queryClient);
};

/**
 * Allows a specific return type to be mapped to useMutation with a special mutation key added for the Session ID.
 * This prevents any potential data persistence across sessions.
 *
 * @template TData
 * @template TVariables
 * @param {UseMutationOptions<TData, TVariables>} options
 * @param {QueryClient | undefined} queryClient
 * @returns {UseMutationResult<TData, TVariables>}
 */
export const OMTSessionUseSpecificMutation = (options, queryClient = undefined) => {
    options.mutationKey = [...(options.mutationKey || []), GetSessionId()];
    return useSpecificMutation(options, queryClient);
};
